// Storefront behaviour: header search toggle, masthead state, gallery arrows
// and swipe, cart / favourites / account store, mini cart drawer, toast,
// filters, quantity, variants and mega menu images.
// Shopify build of the accepted V2 bundle: the cart runs on the Shopify Cart
// AJAX API instead of localStorage, and every route, price format and UI
// string comes from the #ExxenRuntimeData contract that
// snippets/sf-runtime.liquid emits. Favourites stay on the device, as in V2.
// No cross-origin requests.
(() => {
  const WISHLIST_KEY = 'exxen_wishlist_v1';
  const RUNTIME = (() => {
    try { return JSON.parse(document.getElementById('ExxenRuntimeData')?.textContent || '{}'); } catch { return {}; }
  })();
  const ROUTES = RUNTIME.routes || {};
  const STRINGS = RUNTIME.strings || {};
  const SHOP = RUNTIME.shop || {};
  const ROOT = String(ROUTES.root || '/').replace(/\/?$/, '/');
  const PLACEHOLDER = (RUNTIME.assets || {}).placeholder || '';
  // Files that are not product photos (snippets/sf-image-rules.liquid): a cart
  // line or a saved favourite pointing at one shows the placeholder instead.
  const NOT_PHOTO = ((RUNTIME.images || {}).notPhotoKeys || []).filter(Boolean);
  const photoOr = (url, fallback) => (url && !NOT_PHOTO.some((key) => String(url).includes(key)) ? url : fallback);
  const WISHLIST_ENABLED = (RUNTIME.features || {}).wishlist !== false;
  const CART_URL = ROUTES.cart || `${ROOT}cart`;
  const ALL_PRODUCTS_URL = ROUTES.allProducts || `${ROOT}collections/all`;
  const WISHLIST_URL = ROUTES.wishlist || `${ROOT}pages/favorites`;
  const ORDER_URL = ROUTES.order || `${ROOT}pages/siparis`;
  const ORDER_SUCCESS_URL = ROUTES.orderSuccess || `${ROOT}pages/siparis-basarili`;
  const ORDER_STATE_KEY = 'exxen_order_form_v1';
  const ORDER_SUCCESS_STATE_KEY = 'exxen_order_success_v1';
  const DISCOUNT_KEY = 'exxen_discount_code_v1';
  const t = (key, fallback = '') => STRINGS[key] || fallback;
  const fill = (template, values) => String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => (values[name] === undefined ? '' : values[name]));

  // Prices must read exactly as the Liquid `| money` filter renders them, so
  // the shop's own money format drives the client-side formatter too.
  const MONEY_FORMAT = SHOP.moneyFormat || '{{amount_with_comma_separator}} €';
  function groupDigits(cents, places, thousand, decimal) {
    const value = (Math.abs(Number(cents) || 0) / 100).toFixed(places);
    const [whole, fraction] = value.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousand);
    return fraction ? grouped + decimal + fraction : grouped;
  }
  function formatMoney(cents) {
    const amount = Number(cents) || 0;
    const sign = amount < 0 ? '-' : '';
    return sign + MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => {
      switch (name) {
        case 'amount_no_decimals': return groupDigits(amount, 0, ',', '.');
        case 'amount_with_comma_separator': return groupDigits(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return groupDigits(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return groupDigits(amount, 2, "'", '.');
        default: return groupDigits(amount, 2, ',', '.');
      }
    });
  }
  // V2 called money() with a major-unit amount; the adapter keeps that surface.
  const money = (amount) => formatMoney(Math.round(Number(amount || 0) * 100));
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const productHref = (handle, variant = '') => `${ROOT}products/${encodeURIComponent(handle)}${variant ? `?variant=${encodeURIComponent(variant)}` : ''}`;

  // Commerce store. The surface is the one the rest of this bundle already
  // used in V2 - cart.items/add/setQuantity/remove,
  // wishlist.items/has/toggle/remove, account.isSignedIn - but the cart half
  // is now the Shopify Cart AJAX API, so the basket is the real one checkout
  // reads. Favourites stay on the device (localStorage), exactly as the
  // accepted V2 preview behaved.
  const readList = (key) => {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value : [];
    } catch {
      return [];
    }
  };
  const writeList = (key, list) => {
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch {
      /* storage unavailable: state lives for this page only */
    }
  };
  // Shopify cart lines are addressed by their line key; the V2 fallback shape
  // is kept so a line that has not come back from the API yet still resolves.
  const lineKey = (item) => item.key || `${item.handle}::${item.variant || ''}`;
  const listeners = new Set();
  const notify = () => listeners.forEach((listener) => listener());

  // Mirror of the Shopify cart. It is seeded from the runtime contract so the
  // header badge is right on the first paint, then replaced by /cart.js.
  let cartState = { items: [], item_count: Number((RUNTIME.cart || {}).itemCount) || 0, items_subtotal_price: 0, total_price: Number((RUNTIME.cart || {}).totalPrice) || 0 };
  let cartReady = false;
  let cartQueue = Promise.resolve();

  function normaliseLine(line) {
    return {
      key: line.key,
      handle: line.handle,
      title: line.product_title || line.title,
      variant: String(line.variant_id || ''),
      variantLabel: line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : '',
      price: Number(line.final_price || line.price || 0) / 100,
      originalPrice: Number(line.original_price || line.price || 0) / 100,
      originalLinePrice: Number(line.original_line_price || ((line.original_price || line.price || 0) * Number(line.quantity || 0))) / 100,
      linePrice: Number(line.final_line_price || 0) / 100,
      quantity: Number(line.quantity || 0),
      image: photoOr(line.image, ''),
      url: line.url || productHref(line.handle)
    };
  }
  function applyCart(data) {
    if (!data || typeof data !== 'object') return;
    cartState = data;
    cartState.items = (data.items || []).map(normaliseLine);
    cartReady = true;
    notify();
  }
  async function cartRequest(url, body) {
    let response;
    try {
      response = await fetch(url, {
        method: body ? 'POST' : 'GET',
        headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'same-origin'
      });
    } catch {
      throw new Error('CART_TEMPORARY');
    }
    if (!response.ok) {
      if (response.status === 422) throw new Error('CART_STOCK');
      if (response.status === 429) throw new Error('CART_RATE_LIMITED');
      if (response.status === 409) throw new Error('CART_CHANGED');
      throw new Error('CART_TEMPORARY');
    }
    return response.json();
  }
  function enqueue(work) {
    cartQueue = cartQueue.then(work).catch((error) => {
      console.warn(error);
      const message = error.message === 'CART_STOCK' ? t('cartStockError')
        : error.message === 'CART_RATE_LIMITED' ? t('cartRateLimited')
          : error.message === 'CART_CHANGED' ? t('cartChanged')
            : t('cartTemporary', t('cartError'));
      toast(`<strong>${escapeHtml(message)}</strong>`);
      return cartRequest(`${ROOT}cart.js`).then(applyCart).catch(() => {});
    });
    return cartQueue;
  }
  const store = {
    adapter: 'shopify',
    cart: {
      items: () => (cartState.items || []).filter((item) => item && Number(item.quantity) > 0),
      count() { return cartReady ? this.items().reduce((sum, item) => sum + Number(item.quantity || 0), 0) : Number(cartState.item_count) || 0; },
      subtotal() { return Number(cartState.items_subtotal_price || cartState.total_price || 0) / 100; },
      total() { return Number(cartState.total_price || 0) / 100; },
      productSubtotal() { return this.items().reduce((sum, item) => sum + Number(item.originalLinePrice || (item.originalPrice * item.quantity) || 0), 0); },
      ready: () => cartReady,
      load() { return enqueue(() => cartRequest(`${ROOT}cart.js`).then(applyCart)); },
      add(product, quantity = 1) {
        const id = product && (product.variant || product.id);
        if (!id) return Promise.resolve();
        return enqueue(() => cartRequest(`${ROOT}cart/add.js`, { items: [{ id, quantity: Math.max(1, Math.min(99, Math.floor(quantity))) }] })
          .then(() => cartRequest(`${ROOT}cart.js`))
          .then(applyCart));
      },
      setQuantity(key, quantity) {
        return enqueue(() => cartRequest(`${ROOT}cart/change.js`, { id: key, quantity: Math.max(0, Math.min(99, Math.floor(quantity))) }).then(applyCart));
      },
      remove(key) { return this.setQuantity(key, 0); }
    },
    wishlist: {
      items: () => readList(WISHLIST_KEY).filter((item) => item && item.handle),
      has(handle) { return this.items().some((item) => item.handle === handle); },
      toggle(entry) {
        const items = this.items();
        const index = items.findIndex((item) => item.handle === entry.handle);
        if (index >= 0) items.splice(index, 1);
        else items.unshift({ ...entry, addedAt: Date.now() });
        writeList(WISHLIST_KEY, items.slice(0, 200));
        notify();
        return index < 0;
      },
      remove(handle) {
        writeList(WISHLIST_KEY, this.items().filter((item) => item.handle !== handle));
        notify();
      }
    },
    account: { isSignedIn: () => document.body.dataset.account === 'member' },
    subscribe(listener) { listeners.add(listener); return () => listeners.delete(listener); }
  };
  window.ExxenStore = store;
  window.addEventListener('storage', (event) => { if (event.key === WISHLIST_KEY) notify(); });

  function setCount(selector, count) {
    document.querySelectorAll(selector).forEach((node) => {
      node.textContent = String(count);
      node.hidden = count === 0;
    });
  }
  function updateCount() {
    setCount('[data-cart-count]', store.cart.count());
    setCount('[data-wishlist-count]', store.wishlist.items().length);
  }

  let toastTimer = null;
  function toast(html) {
    const node = document.querySelector('[data-toast]');
    if (!node) return;
    node.innerHTML = html;
    node.hidden = false;
    requestAnimationFrame(() => node.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      node.classList.remove('is-visible');
      setTimeout(() => { node.hidden = true; }, 250);
    }, 3200);
  }

  // Header search toggle (mobile).
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-search-toggle]');
    if (!toggle) return;
    const header = toggle.closest('[data-sf-header]');
    const open = !header.classList.contains('is-search-open');
    header.classList.toggle('is-search-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    if (open) header.querySelector('input[type="search"]')?.focus();
  });

  // Add to cart (PDP).
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-add-to-cart]');
    if (!button) return;
    let product;
    try {
      product = JSON.parse(button.dataset.addToCart);
    } catch {
      return;
    }
    const scope = button.closest('.sf-pdp__info') || document;
    const variant = scope.querySelector('input[name="product-variant"]:checked');
    if (variant) {
      product.variant = variant.value;
      product.variantLabel = variant.dataset.variantLabel || variant.closest('label')?.textContent.trim() || '';
      const price = Number(variant.dataset.price);
      if (Number.isFinite(price)) product.price = price;
    }
    const qtyInput = scope.querySelector('#PdpQuantity');
    const quantity = Math.max(1, Math.min(99, Math.floor(Number(qtyInput?.value) || 1)));
    button.disabled = true;
    Promise.resolve(store.cart.add(product, quantity)).then(() => {
      button.disabled = false;
      if (cartDrawer) openMiniCart();
      else toast(`<strong>${escapeHtml(t('addedToCartTitle'))}</strong><span>${escapeHtml(product.title)}</span><a href="${escapeHtml(CART_URL)}">${escapeHtml(t('goToCart'))}</a>`);
    });
  });

  // Quantity steppers.
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-step]');
    if (!step) return;
    const input = step.parentElement.querySelector('input');
    input.value = String(Math.max(1, Math.min(99, (Number(input.value) || 1) + Number(step.dataset.qtyStep))));
  });

  // Variant change updates the visible price.
  const variantPreloads = new Map();
  function prefetchVariantImage(radio) {
    if (!radio) return;
    const url = window.innerWidth < 768 ? radio.dataset.prefetch800 : radio.dataset.prefetch1200;
    if (!url || variantPreloads.has(url)) return;
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'low';
    image.src = url;
    variantPreloads.set(url, image);
  }
  function scheduleVariantPreloads() {
    const radios = [...document.querySelectorAll('input[name="product-variant"][data-image-index]')];
    if (radios.length < 2 || navigator.connection?.saveData) return;
    const selected = Math.max(0, radios.findIndex((radio) => radio.checked));
    const likely = radios.slice(selected + 1).concat(radios.slice(0, selected)).filter((radio) => !radio.disabled && !radio.hasAttribute('data-unavailable')).slice(0, 2);
    const start = () => setTimeout(() => likely.forEach(prefetchVariantImage), 150);
    const mainImage = document.querySelector('[data-sf-gallery] [data-gallery-slide]:not([hidden]) img');
    if (!mainImage || mainImage.complete) start();
    else mainImage.addEventListener('load', start, { once: true });
  }
  document.addEventListener('pointerover', (event) => prefetchVariantImage(event.target.closest('.sf-variant-swatch')?.querySelector('input[name="product-variant"]')), { passive: true });
  document.addEventListener('focusin', (event) => { if (event.target.matches('input[name="product-variant"]')) prefetchVariantImage(event.target); });
  scheduleVariantPreloads();
  document.addEventListener('change', (event) => {
    const radio = event.target.closest('input[name="product-variant"]');
    if (!radio) return;
    prefetchVariantImage(radio);
    const info = radio.closest('.sf-pdp__info');
    const current = info?.querySelector('.sf-price__current');
    if (current && radio.dataset.priceLabel) current.textContent = radio.dataset.priceLabel;
    const compare = info?.querySelector('[data-price-compare]');
    if (compare) compare.hidden = !radio.dataset.compareLabel;
    const selectedLabel = info?.querySelector('[data-variant-selected-label]');
    if (selectedLabel) selectedLabel.textContent = radio.dataset.variantLabel || '';
    info?.querySelectorAll('.sf-variant-swatch').forEach((label) => label.classList.toggle('is-selected', label.contains(radio)));
    const gallery = document.querySelector('[data-sf-gallery]');
    const imageIndex = Number(radio.dataset.imageIndex);
    if (gallery && radio.dataset.imageIndex !== undefined && Number.isInteger(imageIndex)) showSlide(gallery, imageIndex, 'auto');
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', radio.value);
      history.replaceState(null, '', url);
    } catch {
      /* history unavailable */
    }
  });

  // Product gallery arrows and counter (theme.js product-gallery does the switching).
  function galleryIndex(gallery) {
    return Math.max(0, [...gallery.querySelectorAll('[data-gallery-slide]')].findIndex((slide) => !slide.hidden));
  }
  function loadGalleryImage(slide) {
    const img = slide?.querySelector('img[data-src]');
    if (!img) return;
    if (img.dataset.srcset) { img.srcset = img.dataset.srcset; img.removeAttribute('data-srcset'); }
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
  }
  function syncGallery(gallery) {
    const total = gallery.querySelectorAll('[data-gallery-slide]').length;
    const index = galleryIndex(gallery);
    const count = gallery.querySelector('[data-gallery-count]');
    if (count) count.textContent = `${index + 1} / ${total}`;
    gallery.querySelectorAll('.sf-gallery__dot').forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    if (!isSnap(gallery)) gallery.querySelector('[data-gallery-thumb][aria-current="true"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
  // Below 990px the slides form a swipeable scroll-snap track (CSS); arrows,
  // keys and variants scroll the track there instead of switching slides.
  const snapQuery = window.matchMedia('(max-width: 989px)');
  const isSnap = (gallery) => gallery.hasAttribute('data-snap') && snapQuery.matches;
  function showSlide(gallery, index, behavior = '') {
    const total = gallery.querySelectorAll('[data-gallery-slide]').length;
    if (!total) return;
    const target = (index + total) % total;
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    loadGalleryImage(slides[target]);
    if (isSnap(gallery)) {
      const track = gallery.querySelector('[data-gallery-track]');
      track?.scrollTo({ left: target * track.clientWidth, behavior: behavior || (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth') });
      return;
    }
    if (typeof gallery.show === 'function') gallery.show(target);
    syncGallery(gallery);
  }
  document.querySelectorAll('[data-sf-gallery][data-snap]').forEach((gallery) => {
    const track = gallery.querySelector('[data-gallery-track]');
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    if (!track || slides.length < 2) return;
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) { loadGalleryImage(entry.target); observer.unobserve(entry.target); }
      }), { root: track, rootMargin: '0px', threshold: 0.01 });
      slides.forEach((slide) => observer.observe(slide));
    }
    let frame = 0;
    track.addEventListener('scroll', () => {
      if (frame || !isSnap(gallery)) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const index = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
        loadGalleryImage(slides[index]);
        if (!slides[index].hidden) return;
        slides.forEach((slide, i) => { slide.hidden = i !== index; });
        gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
        syncGallery(gallery);
      });
    }, { passive: true });
  });
  document.addEventListener('click', (event) => {
    const gallery = event.target.closest('[data-sf-gallery]');
    if (!gallery) return;
    const step = event.target.closest('[data-gallery-step]');
    if (step) showSlide(gallery, galleryIndex(gallery) + Number(step.dataset.galleryStep));
    else if (event.target.closest('[data-gallery-thumb]')) syncGallery(gallery);
  });
  document.addEventListener('keydown', (event) => {
    const gallery = event.target.closest?.('[data-sf-gallery]');
    if (!gallery || !/^Arrow(Left|Right)$/.test(event.key)) return;
    event.preventDefault();
    showSlide(gallery, galleryIndex(gallery) + (event.key === 'ArrowRight' ? 1 : -1));
  });

  // Full-image lightbox: the whole photo, contained in the viewport and never
  // cropped. The already-loaded 1400px frame shows at once; the 2400px frame
  // replaces it when it arrives. Arrows, keys, swipe, counter, Escape, click
  // outside and a focus trap; closing returns the gallery to the last frame.
  const lightbox = { root: null, gallery: null, opener: null, frames: [], index: 0, swipe: null };
  const lbIcon = (name) => `<svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><use href="#i-${name}"></use></svg>`;
  function lightboxSource(img) {
    const pixels = Math.ceil(Math.max(window.innerWidth, window.innerHeight) * Math.min(3, Math.max(1, window.devicePixelRatio || 1)));
    if (pixels <= 1200) return img.dataset.full1200 || img.dataset.full || img.currentSrc || img.src;
    if (pixels <= 1600) return img.dataset.full1600 || img.dataset.full || img.currentSrc || img.src;
    if (pixels <= 2000) return img.dataset.full2000 || img.dataset.full || img.currentSrc || img.src;
    return img.dataset.full || img.dataset.full2000 || img.currentSrc || img.src;
  }
  function lightboxRoot() {
    if (lightbox.root) return lightbox.root;
    const root = document.createElement('div');
    root.className = 'sf-lightbox';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', t('lightboxLabel', 'Image'));
    root.innerHTML = `<div class="sf-lightbox__stage" data-lb-stage><img class="sf-lightbox__img" alt="" decoding="async" draggable="false" data-lb-img></div>`
      + `<button type="button" class="sf-lightbox__btn sf-lightbox__close" data-lb-close aria-label="${escapeHtml(t('lightboxClose', 'Close'))}">${lbIcon('close')}</button>`
      + `<button type="button" class="sf-lightbox__btn sf-lightbox__nav sf-lightbox__nav--prev" data-lb-step="-1" aria-label="${escapeHtml(t('prevImage', 'Previous'))}">${lbIcon('prev')}</button>`
      + `<button type="button" class="sf-lightbox__btn sf-lightbox__nav sf-lightbox__nav--next" data-lb-step="1" aria-label="${escapeHtml(t('nextImage', 'Next'))}">${lbIcon('next')}</button>`
      + '<p class="sf-lightbox__count" data-lb-count aria-live="polite"></p>';
    root.addEventListener('click', (event) => {
      const step = event.target.closest('[data-lb-step]');
      if (step) { lightboxShow(lightbox.index + Number(step.dataset.lbStep)); return; }
      if (event.target.closest('[data-lb-close]') || !event.target.closest('[data-lb-img]')) closeLightbox();
    });
    // On the document, so the keys keep working after a click moved focus off the dialog.
    document.addEventListener('keydown', (event) => {
      if (root.hidden) return;
      if (event.key === 'Escape') { event.preventDefault(); closeLightbox(); return; }
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); lightboxShow(lightbox.index + (event.key === 'ArrowRight' ? 1 : -1)); return; }
      if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); lightboxShow(event.key === 'Home' ? 0 : lightbox.frames.length - 1); return; }
      if (event.key !== 'Tab') return;
      const nodes = [...root.querySelectorAll('button')].filter((node) => !node.hidden);
      const first = nodes[0]; const last = nodes[nodes.length - 1];
      if (!root.contains(document.activeElement)) { event.preventDefault(); (event.shiftKey ? last : first).focus(); return; }
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    const stage = root.querySelector('[data-lb-stage]');
    stage.addEventListener('pointerdown', (event) => { if (event.isPrimary) lightbox.swipe = { x: event.clientX, y: event.clientY }; });
    stage.addEventListener('pointerup', (event) => {
      const start = lightbox.swipe; lightbox.swipe = null;
      if (!start) return;
      const dx = event.clientX - start.x; const dy = event.clientY - start.y;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        // Only the click this same gesture may still fire is swallowed: a touch
        // swipe usually fires none, and the next tap (close, backdrop) must work.
        lightbox.swallowClick = true;
        clearTimeout(lightbox.swallowTimer);
        lightbox.swallowTimer = setTimeout(() => { lightbox.swallowClick = false; }, 350);
        lightboxShow(lightbox.index + (dx < 0 ? 1 : -1));
      }
    });
    stage.addEventListener('pointercancel', () => { lightbox.swipe = null; });
    // A swipe that ends outside the image must not count as "click outside".
    root.addEventListener('click', (event) => { if (lightbox.swallowClick) { lightbox.swallowClick = false; event.stopImmediatePropagation(); } }, true);
    document.body.appendChild(root);
    lightbox.root = root;
    return root;
  }
  function lightboxShow(index) {
    const total = lightbox.frames.length;
    if (!total) return;
    lightbox.index = (index + total) % total;
    const frame = lightbox.frames[lightbox.index];
    const img = lightbox.root.querySelector('[data-lb-img]');
    const shown = lightbox.index;
    const current = () => lightbox.index === shown && !lightbox.root.hidden;
    // Every frame is decoded before it is painted, so the first frame is already
    // the whole photo (never a partial, progressive or cropped one); the full-size
    // CDN frame then replaces the gallery-size one.
    const paint = (src) => { const next = new Image(); next.decoding = 'async'; next.src = src; return next.decode().catch(() => {}).then(() => { if (current() && next.naturalWidth) img.src = src; }); };
    img.alt = frame.alt;
    if (frame.ready) img.src = frame.preview;
    else { img.removeAttribute('src'); paint(frame.preview); }
    if (frame.full && frame.full !== frame.preview) paint(frame.full);
    [lightbox.index + 1, lightbox.index - 1].forEach((n) => {
      const next = lightbox.frames[(n + total) % total];
      if (next && total > 1) { const pre = new Image(); pre.src = next.preview; }
    });
    lightbox.root.querySelector('[data-lb-count]').textContent = total > 1 ? `${lightbox.index + 1} / ${total}` : '';
  }
  function openLightbox(gallery, index, opener) {
    const frames = [...gallery.querySelectorAll('[data-gallery-slide] img')].map((img) => ({
      preview: img.currentSrc || img.src || img.dataset.src,
      full: lightboxSource(img),
      ready: img.complete && img.naturalWidth > 0,
      alt: img.alt || ''
    }));
    if (!frames.length) return;
    const root = lightboxRoot();
    lightbox.gallery = gallery;
    lightbox.opener = opener || document.activeElement;
    lightbox.frames = frames;
    lightbox.page = { x: window.scrollX, y: window.scrollY };
    const single = frames.length < 2;
    root.querySelectorAll('[data-lb-step]').forEach((button) => { button.hidden = single; });
    root.hidden = false;
    document.body.classList.add('scroll-locked', 'sf-lightbox-open');
    lightboxShow(index);
    requestAnimationFrame(() => { root.dataset.open = 'true'; });
    root.querySelector('[data-lb-close]').focus();
  }
  function closeLightbox() {
    const root = lightbox.root;
    if (!root || root.hidden) return;
    root.dataset.open = 'false';
    root.hidden = true;
    document.body.classList.remove('sf-lightbox-open');
    if (!document.querySelector('[data-drawer-backdrop][data-open="true"]')) document.body.classList.remove('scroll-locked');
    if (lightbox.gallery) {
      // Snap track: jump (the lightbox covered it, nothing to animate) - a smooth
      // scroll would re-hide the focused slide on its way; unhide it now so the
      // focus below lands on it.
      const track = lightbox.gallery.querySelector('[data-gallery-track]');
      if (isSnap(lightbox.gallery) && track) track.scrollTo({ left: lightbox.index * track.clientWidth, behavior: 'instant' });
      else showSlide(lightbox.gallery, lightbox.index);
      const slides = [...lightbox.gallery.querySelectorAll('[data-gallery-slide]')];
      if (isSnap(lightbox.gallery) && slides[lightbox.index]?.hidden) {
        slides.forEach((slide, i) => { slide.hidden = i !== lightbox.index; });
        lightbox.gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === lightbox.index)));
        syncGallery(lightbox.gallery);
      }
    }
    let opener = lightbox.opener;
    // Opened from a photo: focus the photo now on show (the clicked one unless
    // the shopper moved on inside the lightbox), never a hidden slide.
    if (opener?.matches?.('[data-gallery-slide]') && lightbox.gallery) opener = lightbox.gallery.querySelectorAll('[data-gallery-slide]')[lightbox.index] || opener;
    // Syncing the gallery reveals the current thumb (scrollIntoView), which would
    // also glide the page: jump back to exactly where the photo was opened.
    if (lightbox.page) {
      const style = document.documentElement.style;
      const previous = style.scrollBehavior;
      style.scrollBehavior = 'auto';
      window.scrollTo(lightbox.page.x, lightbox.page.y);
      style.scrollBehavior = previous;
    }
    lightbox.gallery = null; lightbox.opener = null; lightbox.frames = []; lightbox.page = null;
    if (opener && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
  }
  document.addEventListener('click', (event) => {
    const gallery = event.target.closest('[data-sf-gallery]');
    if (!gallery) return;
    const opener = event.target.closest('[data-lightbox-open]');
    const slide = event.target.closest('[data-gallery-slide]');
    if (!opener && !(slide && event.target.closest('img'))) return;
    event.preventDefault();
    // Closing returns focus to what was clicked: the expand button, or the slide
    // (focusable, tabindex -1) of the clicked photo - its index is the one shown.
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const index = slide && !opener ? slides.indexOf(slide) : galleryIndex(gallery);
    openLightbox(gallery, index, opener || slide);
  });
  window.ExxenLightbox = { open: openLightbox, close: closeLightbox, state: lightbox };

  // Masthead shadow once the page scrolls.
  const masthead = document.querySelector('[data-sf-masthead]');
  if (masthead) {
    const onScroll = () => masthead.classList.toggle('is-scrolled', window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // Room and sub-category are Shopify collections, not query parameters, so
  // their radios carry no name and never post. A change navigates to the
  // collection and stops the filter form from submitting the rest of the rail.
  document.addEventListener('change', (event) => {
    const nav = event.target.closest('[data-nav-to]');
    if (!nav || !nav.value) return;
    event.stopPropagation();
    window.location.assign(nav.value);
  }, true);

  const nativeSortTimers = new WeakMap();
  function showNativeSortStatus(form) {
    const main = form.querySelector('.sf-listing__main');
    const status = main?.querySelector('[data-sort-status]');
    if (!main || !status) return;
    clearTimeout(nativeSortTimers.get(form));
    status.hidden = false;
    status.dataset.state = 'loading';
    status.querySelector('[data-sort-native-retry]')?.setAttribute('hidden', '');
    main.setAttribute('aria-busy', 'true');
    nativeSortTimers.set(form, setTimeout(() => {
      if (!status.isConnected) return;
      status.dataset.state = 'error';
      const text = status.querySelector('[data-sort-status-text]');
      if (text) text.textContent = status.dataset.errorText || '';
      status.querySelector('[data-sort-native-retry]')?.removeAttribute('hidden');
      main.removeAttribute('aria-busy');
    }, 15000));
  }

  document.addEventListener('click', (event) => {
    const retry = event.target.closest('[data-sort-native-retry]');
    const form = retry?.closest('[data-filter-form]');
    if (!form) return;
    showNativeSortStatus(form);
    form.requestSubmit();
  });

  // Filters: sort submits immediately; on desktop every filter change submits.
  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-filter-form]');
    // The discovery layer (sf-discovery.js) filters in place once it is active.
    if (!form || form.hasAttribute('data-dx-active')) return;
    const isSort = event.target.matches('[data-auto-submit]');
    const desktop = window.matchMedia('(min-width: 990px)').matches;
    if (isSort || (desktop && event.target.type !== 'number')) {
      if (isSort) showNativeSortStatus(form);
      form.querySelectorAll('input[type="number"]').forEach((input) => { if (input.value === '') input.disabled = true; });
      form.querySelectorAll('input[type="radio"]').forEach((input) => { if (input.checked && input.value === '') input.disabled = true; });
      form.requestSubmit();
    }
  });
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-filter-form]');
    if (!form || form.hasAttribute('data-dx-active')) return;
    form.querySelectorAll('input[type="number"]').forEach((input) => { if (input.value === '') input.disabled = true; });
    form.querySelectorAll('input[type="radio"]').forEach((input) => { if (input.checked && input.value === '') input.disabled = true; });
  });

  // Cart lines, shared by the mini cart drawer and the cart page.
  function renderLine(item) {
    const key = escapeHtml(lineKey(item));
    const href = escapeHtml(item.url || productHref(item.handle));
    const linePrice = item.linePrice === undefined ? Number(item.price || 0) * Number(item.quantity) : item.linePrice;
    return `<li class="sf-line">
        <a href="${href}" class="sf-line__img"><img src="${escapeHtml(item.image || PLACEHOLDER)}" alt="" width="96" height="96" loading="lazy" decoding="async"></a>
        <div class="sf-line__info"><a href="${href}">${escapeHtml(item.title)}</a>${item.variantLabel ? `<small>${escapeHtml(item.variantLabel)}</small>` : ''}<span>${money(linePrice)}</span></div>
        <div class="sf-line__actions">
          <div class="sf-qty sf-qty--small"><button type="button" data-line-step="-1" data-line="${key}" aria-label="${escapeHtml(t('decrease'))}">−</button><span aria-live="polite">${item.quantity}</span><button type="button" data-line-step="1" data-line="${key}" aria-label="${escapeHtml(t('increase'))}">+</button></div>
          <button type="button" class="sf-line__remove" data-line-remove="${key}" aria-label="${escapeHtml(`${t('remove')}: ${item.title}`)}">${escapeHtml(t('remove'))}</button>
        </div>
      </li>`;
  }
  const emptyCart = `<div class="sf-empty"><h2>${escapeHtml(t('emptyTitle'))}</h2><p>${escapeHtml(t('emptyText'))}</p><div class="sf-empty__actions"><a class="button" href="${escapeHtml(ALL_PRODUCTS_URL)}">${escapeHtml(t('emptyCta'))}</a></div></div>`;

  function renderMiniCart() {
    const body = document.querySelector('[data-minicart]');
    if (!body) return;
    const items = store.cart.items();
    const count = store.cart.count();
    const foot = document.querySelector('[data-minicart-foot]');
    const summary = document.querySelector('[data-cart-summary]');
    if (summary) summary.textContent = count ? `(${count})` : '';
    if (foot) foot.hidden = !items.length;
    if (!items.length) {
      body.innerHTML = emptyCart;
      return;
    }
    body.innerHTML = `<ul class="sf-lines">${items.map(renderLine).join('')}</ul>`;
    const total = document.querySelector('[data-minicart-total]');
    if (total) total.textContent = money(store.cart.subtotal());
  }

  // Same markup the Liquid cart section renders on the server; the only
  // adaptation to Shopify is that the summary button is a real checkout
  // submit instead of V2's disabled "coming soon" placeholder.
  function renderCartPage(root) {
    const items = store.cart.items();
    if (!items.length) {
      root.innerHTML = emptyCart;
      return;
    }
    const selectedDelivery = document.querySelector('[data-direct-checkout-form] input[name="delivery"]:checked')?.value || 'BG_ADDRESS';
    const pricing = openingOffer(selectedDelivery);
    const discountCode = sessionStorage.getItem(DISCOUNT_KEY) || '';
    root.innerHTML = `<form action="${escapeHtml(CART_URL)}" method="post" novalidate data-cart-form><div class="sf-cart">
      <ul class="sf-lines sf-lines--page">${items.map(renderLine).join('')}</ul>
      <aside class="sf-cart__summary" data-price-summary>
        <h2>${escapeHtml(t('summaryTitle'))}</h2>
        <div class="sf-discount" data-discount-box><label>${escapeHtml(t('discountCode', 'Discount code'))}</label><div><input maxlength="255" autocomplete="off" data-discount-code value="${escapeHtml(discountCode)}"><button type="button" class="button button--secondary" data-discount-apply>${escapeHtml(t('discountApply', 'Apply'))}</button><button type="button" class="sf-link" data-discount-remove${discountCode ? '' : ' hidden'}>${escapeHtml(t('discountRemove', 'Remove'))}</button></div><p data-discount-message role="status"></p></div>
        <div class="sf-cart__row"><span>${escapeHtml(t('productSubtotal'))}</span><strong data-price-subtotal>${formatMoney(pricing.subtotalCents)}</strong></div>
        <div class="sf-cart__row sf-cart__row--discount"><span>${escapeHtml(t('openingDiscount'))}</span><strong data-price-discount>${pricing.discountCents ? `−${formatMoney(pricing.discountCents)}` : formatMoney(0)}</strong></div>
        <div class="sf-cart__row"><span>${escapeHtml(t('delivery'))}</span><strong data-price-delivery>${formatMoney(pricing.shippingCents)}</strong></div>
        <div class="sf-cart__row sf-cart__row--total"><span>${escapeHtml(t('payableTotal'))}</span><strong data-price-total>${formatMoney(pricing.totalCents)}</strong></div>
        ${(RUNTIME.features || {}).directCheckout ? `<a class="button button--accent" href="${escapeHtml(ORDER_URL)}" data-direct-checkout-link>${escapeHtml(t('checkout'))}</a>` : `<button type="submit" name="checkout" class="button button--accent">${escapeHtml(t('checkout'))}</button>`}
        <a href="${escapeHtml(ALL_PRODUCTS_URL)}" class="sf-link">${escapeHtml(t('continue'))}</a>
      </aside>
    </div></form>`;
  }

  function openingOffer(delivery = 'BG_ADDRESS') {
    const subtotalCents = Math.round(store.cart.productSubtotal() * 100);
    const eligible = subtotalCents >= 9900;
    const discountCents = eligible ? Math.floor(subtotalCents * 15 / 100) : 0;
    const shippingCents = delivery === 'EDIRNE_PICKUP' ? 0 : eligible ? 999 : 3000;
    return { subtotalCents, discountCents, shippingCents, totalCents: subtotalCents - discountCents + shippingCents, eligible };
  }

  function updatePriceSummary(delivery = 'BG_ADDRESS') {
    const pricing = openingOffer(delivery);
    document.querySelectorAll('[data-price-summary]').forEach((summary) => {
      const set = (selector, value) => { const node = summary.querySelector(selector); if (node) node.textContent = value; };
      set('[data-price-subtotal]', formatMoney(pricing.subtotalCents));
      set('[data-price-discount]', pricing.discountCents ? `−${formatMoney(pricing.discountCents)}` : formatMoney(0));
      set('[data-price-delivery]', formatMoney(pricing.shippingCents));
      set('[data-price-total]', formatMoney(pricing.totalCents));
    });
    return pricing;
  }

  const orderApiRoot = () => {
    const base = String((RUNTIME.services || {}).orderApi || '').replace(/\/+$/, '');
    return base.endsWith('/checkout-api') ? base : `${base}/checkout-api`;
  };
  let quoteSequence = 0;
  function applyPriceSummary(pricing) {
    document.querySelectorAll('[data-price-summary]').forEach((summary) => {
      const set = (selector, value) => { const node = summary.querySelector(selector); if (node) node.textContent = value; };
      set('[data-price-subtotal]', formatMoney(pricing.subtotalCents));
      set('[data-price-discount]', pricing.discountCents ? `−${formatMoney(pricing.discountCents)}` : formatMoney(0));
      set('[data-price-delivery]', formatMoney(pricing.shippingCents));
      set('[data-price-total]', formatMoney(pricing.totalCents));
    });
  }
  async function syncDiscountQuote({ announce = false } = {}) {
    if (!(RUNTIME.features || {}).directCheckout || !store.cart.ready() || !store.cart.items().length) return;
    const code = sessionStorage.getItem(DISCOUNT_KEY) || '';
    const form = document.querySelector('[data-direct-checkout-form]');
    const delivery = form?.elements.delivery?.value || 'BG_ADDRESS';
    const sequence = ++quoteSequence;
    const boxes = document.querySelectorAll('[data-discount-box]');
    boxes.forEach((box) => { const message = box.querySelector('[data-discount-message]'); if (message) message.textContent = t('discountChecking'); });
    const customer = form?.elements.email?.value ? { firstName: form.elements.firstName.value, lastName: form.elements.lastName.value, phoneCountry: form.elements.phoneCountry.value, phone: form.elements.phone.value, email: form.elements.email.value } : null;
    try {
      const response = await fetch(`${orderApiRoot()}/quote`, { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify({ locale: SHOP.locale === 'tr' ? 'tr' : 'bg', delivery, discountCode: code, customer, items: store.cart.items().map((item) => ({ variantId: item.variant, quantity: item.quantity })) }) });
      const body = await response.json().catch(() => ({}));
      if (sequence !== quoteSequence) return;
      if (!response.ok || !body.totals) throw new Error(body.error || 'QUOTE_FAILED');
      applyPriceSummary(body.totals);
      boxes.forEach((box) => { const message = box.querySelector('[data-discount-message]'); const remove = box.querySelector('[data-discount-remove]'); if (message) message.textContent = code ? t('discountApplied') : ''; if (remove) remove.hidden = !code; });
    } catch (failure) {
      if (sequence !== quoteSequence) return;
      applyPriceSummary(openingOffer(delivery));
      const definitive = failure.message === 'DISCOUNT_NOT_APPLICABLE';
      boxes.forEach((box) => { const message = box.querySelector('[data-discount-message]'); if (message) message.textContent = definitive ? t('discountNotEligible') : failure.message === 'RATE_LIMITED' ? t('discountRateLimited') : t('discountUnavailable'); });
      if (definitive) sessionStorage.removeItem(DISCOUNT_KEY);
    }
  }

  document.addEventListener('click', (event) => {
    const apply = event.target.closest('[data-discount-apply]');
    const remove = event.target.closest('[data-discount-remove]');
    if (!apply && !remove) return;
    const box = event.target.closest('[data-discount-box]');
    const input = box?.querySelector('[data-discount-code]');
    if (remove) {
      sessionStorage.removeItem(DISCOUNT_KEY); if (input) input.value = ''; remove.hidden = true;
      const message = box?.querySelector('[data-discount-message]'); if (message) message.textContent = t('discountRemoved');
      const delivery = document.querySelector('[data-direct-checkout-form]')?.elements.delivery?.value || 'BG_ADDRESS'; applyPriceSummary(openingOffer(delivery)); return;
    }
    const code = input?.value.trim() || '';
    if (!code) return;
    sessionStorage.setItem(DISCOUNT_KEY, code); syncDiscountQuote({ announce: true });
  });

  function normalizeCheckoutPhone(country, value) {
    const raw = String(value || '').trim();
    if (!raw || !/^\+?[0-9\s().-]+$/.test(raw)) return '';
    let digits = raw.replace(/\D/g, '');
    if (country === 'BG') {
      if (digits.startsWith('00359')) digits = digits.slice(5);
      else if (digits.startsWith('359')) digits = digits.slice(3);
      else if (digits.startsWith('0')) digits = digits.slice(1);
      return /^[2-9]\d{7,8}$/.test(digits) ? `+359${digits}` : '';
    }
    if (country === 'TR') {
      if (digits.startsWith('0090')) digits = digits.slice(4);
      else if (digits.startsWith('90')) digits = digits.slice(2);
      else if (digits.startsWith('0')) digits = digits.slice(1);
      return /^[2-5]\d{9}$/.test(digits) ? `+90${digits}` : '';
    }
    return '';
  }

  function checkoutAddress(form, phone, prefix = '') {
    const field = (name) => { const key = prefix ? `${prefix}${name[0].toUpperCase()}${name.slice(1)}` : name; return form.elements[key]?.value?.trim() || ''; };
    return { firstName: form.elements.firstName.value.trim(), lastName: form.elements.lastName.value.trim(), phone, city: field('city'), zip: field('zip'), address1: field('address1'), address2: field('address2'), countryCode: 'BG' };
  }

  function stableJson(value) {
    if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
    if (value && typeof value === 'object') return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableJson(value[key])}`).join(',')}}`;
    return JSON.stringify(value);
  }

  async function payloadFingerprint(payload) {
    const canonical = stableJson({ ...payload, idempotencyKey: undefined });
    if (crypto.subtle) {
      const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical));
      return [...new Uint8Array(bytes)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
    }
    let value = 2166136261;
    for (let index = 0; index < canonical.length; index += 1) value = Math.imul(value ^ canonical.charCodeAt(index), 16777619);
    return `fallback_${(value >>> 0).toString(16)}`;
  }

  async function orderIdempotency(storageKey, payload) {
    const fingerprint = await payloadFingerprint(payload);
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      if (saved?.schema === 3 && saved.uncertain && saved.fingerprint !== fingerprint) return { blocked: true, key: saved.key || '', transactionId: saved.transactionId || '' };
      if (saved?.schema === 3 && saved.fingerprint === fingerprint && /^[a-zA-Z0-9_-]{16,80}$/.test(saved.key)) return { key: saved.key, fingerprint };
    } catch { /* replace legacy or malformed state */ }
    const key = crypto.randomUUID().replace(/-/g, '');
    sessionStorage.setItem(storageKey, JSON.stringify({ schema: 3, fingerprint, key, uncertain: false }));
    return { key, fingerprint };
  }

  function updateIdempotency(storageKey, updates) {
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      if (saved?.schema === 3) sessionStorage.setItem(storageKey, JSON.stringify({ ...saved, ...updates }));
    } catch { /* a future submit safely creates a new state */ }
  }

  function successState(value) {
    if (!value || value.schema !== 1 || typeof value.orderName !== 'string' || !value.orderName.trim()) return null;
    if (!['BG_ADDRESS', 'EDIRNE_PICKUP'].includes(value.delivery)) return null;
    if (!Array.isArray(value.lines) || !value.lines.length || value.lines.length > 100) return null;
    const lines = value.lines.map((line) => ({
      title: String(line?.title || '').trim().slice(0, 300),
      variantLabel: String(line?.variantLabel || '').trim().slice(0, 200),
      quantity: Math.max(1, Math.min(99, Math.floor(Number(line?.quantity) || 0)))
    }));
    if (lines.some((line) => !line.title || !line.quantity)) return null;
    const totals = {};
    for (const key of ['subtotalCents', 'discountCents', 'shippingCents', 'totalCents']) {
      const amount = Number(value.totals?.[key]);
      if (!Number.isSafeInteger(amount) || amount < 0 || amount > 1000000000) return null;
      totals[key] = amount;
    }
    const address = value.delivery === 'BG_ADDRESS'
      ? ['address1', 'address2', 'city', 'zip'].reduce((out, key) => ({ ...out, [key]: String(value.address?.[key] || '').trim().slice(0, 180) }), {})
      : null;
    if (value.delivery === 'BG_ADDRESS' && (!address.address1 || !address.city || !address.zip)) return null;
    return { schema: 1, orderName: value.orderName.trim().slice(0, 80), delivery: value.delivery, address, lines, totals };
  }

  function renderOrderSuccessPage() {
    const root = document.querySelector('[data-order-success-content]');
    if (!root) return;
    let state = null;
    try { state = successState(JSON.parse(sessionStorage.getItem(ORDER_SUCCESS_STATE_KEY) || 'null')); } catch { /* show the safe empty state */ }
    if (!state) return;
    const delivery = state.delivery === 'EDIRNE_PICKUP'
      ? t('checkoutPickupDelivery')
      : [state.address.address1, state.address.address2, state.address.city, state.address.zip].filter(Boolean).join(', ');
    const payment = state.delivery === 'EDIRNE_PICKUP' ? t('checkoutPayStore') : t('checkoutCodBg');
    root.innerHTML = `<div class="sf-order-success__icon" aria-hidden="true"><svg viewBox="0 0 48 48" role="img"><circle cx="24" cy="24" r="22"></circle><path d="m14 24 7 7 14-15"></path></svg></div>
      <div class="sf-order-success__head"><h1>${escapeHtml(t('checkoutSuccessTitle'))}</h1><p class="sf-order-success__number">${escapeHtml(t('checkoutOrderNumber'))}: <strong>${escapeHtml(state.orderName.replace(/^#/, ''))}</strong></p></div>
      <div class="sf-order-success__grid">
        <section class="sf-order-success__card"><h2>${escapeHtml(t('checkoutProducts'))}</h2><ul class="sf-order-success__products">${state.lines.map((line) => `<li><div><strong>${escapeHtml(line.title)}</strong>${line.variantLabel && line.variantLabel !== 'Default Title' ? `<span>${escapeHtml(line.variantLabel)}</span>` : ''}</div><span>× ${line.quantity}</span></li>`).join('')}</ul></section>
        <section class="sf-order-success__card"><dl><div><dt>${escapeHtml(t('checkoutDelivery'))}</dt><dd>${escapeHtml(delivery)}</dd></div><div><dt>${escapeHtml(t('checkoutPayment'))}</dt><dd>${escapeHtml(payment)}</dd></div></dl><p class="sf-order-success__notice">${escapeHtml(t('checkoutNoPrepayment'))}</p></section>
        <section class="sf-order-success__card sf-order-success__totals"><dl><div><dt>${escapeHtml(t('checkoutSubtotal'))}</dt><dd>${formatMoney(state.totals.subtotalCents)}</dd></div><div><dt>${escapeHtml(t('checkoutDiscount'))}</dt><dd>${state.totals.discountCents ? `−${formatMoney(state.totals.discountCents)}` : formatMoney(0)}</dd></div><div><dt>${escapeHtml(t('checkoutShipping'))}</dt><dd>${formatMoney(state.totals.shippingCents)}</dd></div><div class="sf-order-success__total"><dt>${escapeHtml(t('checkoutTotal'))}</dt><dd>${formatMoney(state.totals.totalCents)}</dd></div></dl></section>
      </div>
      <div class="sf-order-success__actions"><a class="button button--accent" href="${escapeHtml(ALL_PRODUCTS_URL)}">${escapeHtml(t('checkoutContinueShopping'))}</a><a class="button button--secondary" href="https://wa.me/359877450520" target="_blank" rel="noopener noreferrer">${escapeHtml(t('checkoutWhatsappSupport'))}</a></div>`;
  }

  renderOrderSuccessPage();

  const checkoutFieldRules = {
    firstName: (value) => value.trim() ? '' : t('checkoutRequiredField'),
    lastName: (value) => value.trim() ? '' : t('checkoutRequiredField'),
    email: (value) => /^\S+@\S+\.\S+$/.test(value.trim()) ? '' : t('checkoutInvalidEmail'),
    phoneCountry: (value) => ['BG', 'TR'].includes(value) ? '' : t('checkoutInvalidPhone'),
    phone: (value, form) => normalizeCheckoutPhone(form.elements.phoneCountry.value, value) ? '' : t('checkoutInvalidPhone'),
    city: (value) => value.trim() ? '' : t('checkoutRequiredField'),
    zip: (value) => value.trim() ? '' : t('checkoutRequiredField'),
    address1: (value) => value.trim() ? '' : t('checkoutRequiredField'),
    billingCity: (value) => value.trim() ? '' : t('checkoutRequiredField'),
    billingZip: (value) => value.trim() ? '' : t('checkoutRequiredField'),
    billingAddress1: (value) => value.trim() ? '' : t('checkoutRequiredField')
  };

  function checkoutFieldEnabled(form, name) {
    if (['city', 'zip', 'address1'].includes(name)) return form.elements.delivery.value === 'BG_ADDRESS';
    if (name.startsWith('billing')) return form.elements.billingDifferent.checked;
    return true;
  }

  function checkoutFieldError(form, name, message = '') {
    const field = form.elements[name];
    if (!field || field instanceof RadioNodeList) return;
    const label = field.closest('label');
    let error = label?.querySelector(`[data-field-error="${name}"]`);
    if (!error && label) {
      error = document.createElement('small');
      error.className = 'sf-field-error';
      error.dataset.fieldError = name;
      error.id = `Checkout${name[0].toUpperCase()}${name.slice(1)}Error`;
      error.setAttribute('role', 'alert');
      label.append(error);
    }
    const invalid = Boolean(message);
    field.classList.toggle('is-invalid', invalid);
    if (invalid) {
      field.setAttribute('aria-invalid', 'true');
      field.setAttribute('aria-describedby', error.id);
      error.textContent = message;
      error.hidden = false;
    } else {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
      if (error) { error.textContent = ''; error.hidden = true; }
    }
  }

  function updateCheckoutPhonePlaceholder(form) {
    const phone = form.elements.phone;
    const country = form.elements.phoneCountry?.value;
    if (!phone) return;
    phone.placeholder = country === 'TR' ? phone.dataset.placeholderTr || '' : country === 'BG' ? phone.dataset.placeholderBg || '' : '';
  }

  function checkoutSubmitState(form, state) {
    const submit = form.querySelector('button[type="submit"]');
    const label = submit?.querySelector('[data-checkout-submit-label]');
    const spinner = submit?.querySelector('[data-checkout-spinner]');
    const status = form.querySelector('[data-direct-checkout-status]');
    if (!submit || !label || !status) return;
    const messages = {
      idle: t('checkoutPlaceOrder'),
      sending: t('checkoutSubmitting'),
      checking: t('checkoutChecking'),
      success: t('checkoutRedirecting')
    };
    const busy = state !== 'idle';
    form.dataset.submitState = state;
    submit.disabled = busy;
    submit.setAttribute('aria-busy', String(busy));
    label.textContent = messages[state] || messages.idle;
    if (spinner) spinner.hidden = !busy || state === 'success';
    status.textContent = state === 'idle' ? '' : messages[state] || '';
    status.hidden = state === 'idle';
  }

  function validateCheckoutForm(form, { focus = true } = {}) {
    const invalid = [];
    Object.entries(checkoutFieldRules).forEach(([name, rule]) => {
      const enabled = checkoutFieldEnabled(form, name);
      const message = enabled ? rule(form.elements[name]?.value || '', form) : '';
      checkoutFieldError(form, name, message);
      if (message) invalid.push(form.elements[name]);
    });
    const delivery = form.elements.delivery.value;
    const deliveryGroup = form.querySelector('.sf-checkout__delivery');
    const deliveryInvalid = !['BG_ADDRESS', 'EDIRNE_PICKUP'].includes(delivery);
    deliveryGroup?.classList.toggle('is-invalid', deliveryInvalid);
    if (deliveryInvalid) invalid.push(deliveryGroup);
    if (focus && invalid[0]) {
      const target = invalid[0];
      const rect = target.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof target.focus === 'function') requestAnimationFrame(() => target.focus({ preventScroll: true }));
    }
    return invalid.length === 0;
  }

  const checkoutForm = document.querySelector('[data-direct-checkout-form]');
  if (checkoutForm) {
    try {
      const saved = JSON.parse(sessionStorage.getItem(ORDER_STATE_KEY) || '{}');
      Object.entries(saved).forEach(([name, value]) => {
        const field = checkoutForm.elements[name]; if (!field) return;
        if (field instanceof RadioNodeList) { const option = [...field].find((item) => item.value === value); if (option) option.checked = true; }
        else if (field.type === 'checkbox') field.checked = Boolean(value);
        else field.value = String(value ?? '');
      });
    } catch { /* keep the blank guest form */ }
    updateCheckoutPhonePlaceholder(checkoutForm);
    const discountInput = document.querySelector('[data-discount-code]');
    if (discountInput) discountInput.value = sessionStorage.getItem(DISCOUNT_KEY) || '';
    checkoutForm.dispatchEvent(new Event('change', { bubbles: true }));
    const saveCheckoutForm = () => {
      const saved = {}; new FormData(checkoutForm).forEach((value, key) => { saved[key] = value; }); saved.billingDifferent = checkoutForm.elements.billingDifferent.checked;
      sessionStorage.setItem(ORDER_STATE_KEY, JSON.stringify(saved));
    };
    checkoutForm.addEventListener('input', saveCheckoutForm);
    checkoutForm.addEventListener('change', saveCheckoutForm);
    const validateEditedField = (event) => {
      if (checkoutFieldRules[event.target.name]) checkoutFieldError(checkoutForm, event.target.name, checkoutFieldEnabled(checkoutForm, event.target.name) ? checkoutFieldRules[event.target.name](event.target.value, checkoutForm) : '');
      if (event.target.name === 'phoneCountry') checkoutFieldError(checkoutForm, 'phone', checkoutFieldRules.phone(checkoutForm.elements.phone.value, checkoutForm));
    };
    checkoutForm.addEventListener('input', validateEditedField);
    checkoutForm.addEventListener('change', (event) => {
      if (event.target.name === 'phoneCountry') { updateCheckoutPhonePlaceholder(checkoutForm); validateEditedField(event); }
    });
  }

  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-direct-checkout-form]'); if (!form) return;
    if (event.target.name === 'delivery') {
      const pickup = event.target.value === 'EDIRNE_PICKUP'; const block = form.querySelector('[data-bg-address]'); block.hidden = pickup;
      block.querySelectorAll('input').forEach((input) => { input.required = !pickup && !input.name.endsWith('2'); }); updatePriceSummary(event.target.value); syncDiscountQuote();
      if (pickup) ['city', 'zip', 'address1'].forEach((name) => checkoutFieldError(form, name));
    }
    if (event.target.name === 'billingDifferent') {
      const block = form.querySelector('[data-billing-address]'); block.hidden = !event.target.checked;
      block.querySelectorAll('input').forEach((input) => { input.required = event.target.checked && !input.name.endsWith('2'); });
    }
  });

  // The order is created only once Shopify confirms it on the server; the
  // browser never assumes success. One idempotency key per cart/form payload
  // makes a repeated click, reload or status check reuse the same order.
  const ORDER_IDEMPOTENCY_KEY = 'exxen_order_idempotency_v3';
  const CART_CLEAR_PENDING_KEY = 'exxen_cart_clear_pending_v1';
  // A normal order takes 4.5–9 s on the server; a single Shopify call may take
  // up to 30 s before the server gives up and reports it as uncertain.
  const ORDER_REQUEST_TIMEOUT_MS = 45000;
  // Same-key status checks after an uncertain result: bounded (~2 minutes),
  // never a new create.
  const ORDER_STATUS_DELAYS_MS = [3000, 5000, 8000, 10000, 15000, 20000, 30000, 30000];

  // The verified order no longer waits for cart/clear.js before redirecting:
  // the clear is sent with keepalive and finished on the next page if needed.
  function clearCartAfterOrder(order) {
    const lines = {};
    for (const line of order.lines || []) { const id = String(line.variantId || '').split('/').pop(); if (id) lines[id] = (lines[id] || 0) + Number(line.quantity || 0); }
    try { sessionStorage.setItem(CART_CLEAR_PENDING_KEY, JSON.stringify({ at: Date.now(), lines })); } catch { /* the keepalive request still runs */ }
    try {
      fetch(`${ROOT}cart/clear.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: '{}', credentials: 'same-origin', keepalive: true }).catch(() => {});
    } catch { /* finished on the next page */ }
  }

  // Runs once, on the first page after the order, and removes only the ordered
  // quantities: lines added after the order are never cleared.
  function finishPendingCartClear() {
    let pending = null;
    try { pending = JSON.parse(sessionStorage.getItem(CART_CLEAR_PENDING_KEY) || 'null'); sessionStorage.removeItem(CART_CLEAR_PENDING_KEY); } catch { return; }
    if (!pending?.lines || Date.now() - Number(pending.at) > 10 * 60 * 1000) return;
    const attempt = (left) => cartRequest(`${ROOT}cart.js`).then((cart) => {
      const updates = {}; const remaining = { ...pending.lines };
      for (const line of cart.items || []) {
        const take = Math.min(remaining[line.variant_id] || 0, line.quantity);
        if (take > 0) { remaining[line.variant_id] -= take; updates[line.key] = line.quantity - take; }
      }
      return Object.keys(updates).length ? cartRequest(`${ROOT}cart/update.js`, { updates }) : cart;
    }).then(applyCart).catch(() => { if (left > 0) setTimeout(() => attempt(left - 1), 2000); });
    attempt(2);
  }

  finishPendingCartClear();

  function completeOrderSuccess(form, order) {
    const localized = new Map(store.cart.items().map((line) => [String(line.variant), line]));
    const verifiedSummary = successState({
      schema: 1,
      orderName: order.orderName,
      delivery: order.delivery,
      address: order.shippingAddress,
      lines: (order.lines || []).map((line) => {
        const local = localized.get(String(line.variantId));
        return { title: local?.title || line.title, variantLabel: local?.variantLabel || '', quantity: line.quantity };
      }),
      totals: order.totals
    });
    // The server verified the order; an unexpected summary shape only costs the
    // detailed success page, never a "not created" message.
    if (verifiedSummary) sessionStorage.setItem(ORDER_SUCCESS_STATE_KEY, JSON.stringify(verifiedSummary));
    else sessionStorage.removeItem(ORDER_SUCCESS_STATE_KEY);
    sessionStorage.removeItem(ORDER_STATE_KEY); sessionStorage.removeItem(DISCOUNT_KEY); sessionStorage.removeItem(ORDER_IDEMPOTENCY_KEY);
    checkoutSubmitState(form, 'success');
    clearCartAfterOrder(order);
    window.location.assign(ORDER_SUCCESS_URL);
  }

  const referenceText = (reference) => `${t('checkoutReference')}: ${reference || t('checkoutReferenceUnavailable')}`;

  function checkoutFailureMessage(reason, body, reference, uncertain) {
    return reason === 'INSUFFICIENT_STOCK'
      ? body.item ? fill(t('checkoutStockDetail'), { product: body.item.title, available: body.item.available }) : t('checkoutStockError')
      : reason === 'VARIANT_NOT_FOR_SALE' || reason === 'VARIANT_NOT_FOUND'
        ? t('checkoutVariantUnavailable')
      : reason === 'BG_ADDRESS_REQUIRED' || reason === 'BILLING_ADDRESS_REQUIRED'
        ? t('checkoutAddressError')
        : reason === 'INVALID_PHONE'
          ? t('checkoutInvalidPhone')
        : reason === 'CONTACT_REQUIRED'
          ? t('checkoutContactError', t('checkoutError'))
        : reason === 'INVALID_DELIVERY'
          ? t('checkoutDeliveryError', t('checkoutError'))
        : reason === 'IDEMPOTENCY_CONFLICT' || reason === 'IDEMPOTENCY_REFRESH_REQUIRED'
          ? t('checkoutCartChangedError', t('checkoutError'))
        : reason === 'DISCOUNT_NOT_APPLICABLE'
          ? t('discountNotEligible')
        : reason === 'RATE_LIMITED'
          ? t('checkoutRateLimited')
        : reason === 'MISSING_SCOPES' || reason === 'DISABLED'
          ? `${t('checkoutServiceUnavailable')} ${referenceText(reference)}`
        : body.outcome === 'NOT_CREATED'
          ? `${t('checkoutNotCreated', t('checkoutError'))} ${referenceText(reference)}`
        : uncertain
          ? `${t('checkoutStatusUnknown')} ${referenceText(reference)}`
          : `${t('checkoutError')} ${referenceText(reference)}`;
  }

  // Asks the server what happened to this key. Only COMPLETED (verified in
  // Shopify), NOT_CREATED and REJECTED end the check; anything else keeps the
  // "checking, do not order again" state, also after the budget runs out.
  let orderStatusPoll = 0;
  async function pollOrderStatus(form, { key, reference }) {
    const error = form.querySelector('[data-direct-checkout-error]');
    const poll = ++orderStatusPoll;
    error.hidden = true;
    checkoutSubmitState(form, 'checking');
    for (const delay of ORDER_STATUS_DELAYS_MS) {
      await new Promise((resolve) => window.setTimeout(resolve, delay));
      if (poll !== orderStatusPoll) return;
      let body = {};
      try {
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), 20000);
        try {
          const headers = { 'content-type': 'application/json', accept: 'application/json' };
          if (reference) headers['x-transaction-id'] = reference;
          const response = await fetch(`${orderApiRoot()}/orders/status`, { method: 'POST', headers, body: JSON.stringify({ idempotencyKey: key }), signal: controller.signal });
          body = await response.json().catch(() => ({}));
        } finally { window.clearTimeout(timer); }
      } catch { continue; }
      if (poll !== orderStatusPoll) return;
      if (body.outcome === 'COMPLETED' && body.order?.orderName) { completeOrderSuccess(form, body.order); return; }
      if (body.outcome === 'NOT_CREATED' || body.outcome === 'REJECTED') {
        updateIdempotency(ORDER_IDEMPOTENCY_KEY, { uncertain: false });
        error.textContent = checkoutFailureMessage(String(body.error || 'ORDER_FAILED'), body, reference, false);
        error.hidden = false;
        checkoutSubmitState(form, 'idle');
        return;
      }
    }
    if (poll !== orderStatusPoll) return;
    error.textContent = `${t('checkoutStatusUnknown')} ${referenceText(reference)}`;
    error.hidden = false;
    // Checks have stopped: no spinner that suggests otherwise.
    const spinner = form.querySelector('[data-checkout-spinner]');
    if (spinner) spinner.hidden = true;
  }

  // A reload while an order was in flight or uncertain resumes the check with
  // the same key instead of offering a fresh submit.
  if (checkoutForm) {
    try {
      const saved = JSON.parse(sessionStorage.getItem(ORDER_IDEMPOTENCY_KEY) || 'null');
      if (saved?.schema === 3 && saved.uncertain && /^[a-zA-Z0-9_-]{16,80}$/.test(saved.key || '')) pollOrderStatus(checkoutForm, { key: saved.key, reference: saved.transactionId || '' });
    } catch { /* nothing to resume */ }
  }

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-direct-checkout-form]'); if (!form) return;
    event.preventDefault();
    const error = form.querySelector('[data-direct-checkout-error]'); const submit = form.querySelector('button[type="submit"]');
    if (submit.disabled) return;
    error.hidden = true;
    if (!validateCheckoutForm(form)) return;
    checkoutSubmitState(form, 'sending');
    if (!store.cart.ready()) await store.cart.load();
    if (!store.cart.items().length) { error.textContent = t('checkoutEmptyCart'); error.hidden = false; checkoutSubmitState(form, 'idle'); return; }
    const delivery = form.elements.delivery.value; const billingDifferent = form.elements.billingDifferent.checked;
    const normalizedPhone = normalizeCheckoutPhone(form.elements.phoneCountry.value, form.elements.phone.value);
    if (!normalizedPhone) { checkoutFieldError(form, 'phone', t('checkoutInvalidPhone')); form.elements.phone.focus(); checkoutSubmitState(form, 'idle'); return; }
    const storageKey = ORDER_IDEMPOTENCY_KEY;
    const transactionId = crypto.randomUUID();
    const payload = {
      locale: SHOP.locale === 'tr' ? 'tr' : 'bg', delivery,
      items: store.cart.items().map((item) => ({ variantId: item.variant, quantity: item.quantity })),
      customer: { firstName: form.elements.firstName.value.trim(), lastName: form.elements.lastName.value.trim(), phoneCountry: form.elements.phoneCountry.value, phone: normalizedPhone, email: form.elements.email.value.trim() },
      shippingAddress: delivery === 'BG_ADDRESS' ? checkoutAddress(form, normalizedPhone) : null,
      billingDifferent, billingAddress: billingDifferent ? checkoutAddress(form, normalizedPhone, 'billing') : null,
      discountCode: sessionStorage.getItem(DISCOUNT_KEY) || '',
      note: form.elements.note.value.trim()
    };
    const idempotency = await orderIdempotency(storageKey, payload);
    if (idempotency.blocked) {
      // An earlier order is still unverified: check it, never create another.
      pollOrderStatus(form, { key: idempotency.key, reference: idempotency.transactionId });
      return;
    }
    payload.idempotencyKey = idempotency.key;
    updateIdempotency(storageKey, { uncertain: true, transactionId });
    try {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), ORDER_REQUEST_TIMEOUT_MS);
      let response;
      try {
        response = await fetch(`${orderApiRoot()}/orders`, { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json', 'x-transaction-id': transactionId }, body: JSON.stringify(payload), signal: controller.signal });
      } finally { window.clearTimeout(timer); }
      const body = await response.json().catch(() => ({}));
      if (!response.ok || !body.order?.orderName) {
        const failure = new Error(body.error || (response.status === 429 ? 'RATE_LIMITED' : 'ORDER_STATUS_UNKNOWN'));
        failure.body = body; failure.status = response.status; throw failure;
      }
      completeOrderSuccess(form, body.order);
    } catch (failure) {
      const reason = String(failure.message || 'ORDER_FAILED');
      const body = failure.body || {};
      const reference = body.transactionId || transactionId;
      // NOT_CREATED is definite (nothing reached Shopify); a lost response,
      // timeout or PENDING_VERIFICATION is not, and is checked with the same key.
      const uncertain = body.outcome !== 'NOT_CREATED' && (body.outcome === 'PENDING_VERIFICATION' || !failure.status || reason === 'ORDER_STATUS_UNKNOWN');
      updateIdempotency(storageKey, { uncertain, transactionId: reference });
      if (uncertain) { pollOrderStatus(form, { key: idempotency.key, reference }); return; }
      if (reason === 'INVALID_PHONE') {
        checkoutFieldError(form, 'phone', t('checkoutInvalidPhone'));
        checkoutFieldError(form, 'phoneCountry', ['BG', 'TR'].includes(form.elements.phoneCountry.value) ? '' : t('checkoutInvalidPhone'));
      }
      error.textContent = checkoutFailureMessage(reason, body, reference, false);
      error.hidden = false;
      checkoutSubmitState(form, 'idle');
    }
  });

  // Favourites page.
  function wishlistImageUrl(url, width) {
    const value = String(url || '');
    if (!value || value === PLACEHOLDER || !value.includes('/cdn/shop/')) return value;
    try {
      const parsed = new URL(value, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.href;
    } catch (_) {
      return value;
    }
  }

  function wishlistImageMarkup(item) {
    const image = photoOr(item.image, PLACEHOLDER);
    const src = wishlistImageUrl(image, 480);
    const responsive = image !== PLACEHOLDER && image.includes('/cdn/shop/')
      ? ` srcset="${[240, 360, 480, 640, 960].map((width) => `${escapeHtml(wishlistImageUrl(image, width))} ${width}w`).join(', ')}" sizes="(min-width: 1280px) 33vw, 50vw"`
      : '';
    return `<img src="${escapeHtml(src)}"${responsive} alt="" width="480" height="480" loading="lazy" decoding="async">`;
  }

  function renderWishlist(root) {
    const items = store.wishlist.items();
    if (!items.length) {
      root.innerHTML = `<div class="sf-empty"><h2>${escapeHtml(t('wishlistEmptyTitle'))}</h2><p>${escapeHtml(t('wishlistEmptyText'))}</p><div class="sf-empty__actions"><a class="button" href="${escapeHtml(ALL_PRODUCTS_URL)}">${escapeHtml(t('wishlistEmptyCta'))}</a></div></div>`;
      return;
    }
    root.innerHTML = `<ul class="sf-wishlist">${items.map((item) => `<li class="sf-wish">
        <a class="sf-wish__img" href="${productHref(item.handle, item.variant)}">${wishlistImageMarkup(item)}</a>
        <div class="sf-wish__body">${item.vendor ? `<small>${escapeHtml(item.vendor)}</small>` : ''}<a href="${productHref(item.handle, item.variant)}">${escapeHtml(item.title)}</a>${item.variantLabel ? `<small>${escapeHtml(item.variantLabel)}</small>` : ''}${item.priceLabel ? `<span>${escapeHtml(item.priceLabel)}</span>` : ''}</div>
        <div class="sf-wish__actions"><a class="button button--secondary" href="${productHref(item.handle, item.variant)}">${escapeHtml(t('wishlistView'))}</a><button type="button" class="sf-line__remove" data-wishlist-remove="${escapeHtml(item.handle)}" aria-label="${escapeHtml(`${t('wishlistRemove')}: ${item.title}`)}">${escapeHtml(t('wishlistRemove'))}</button></div>
      </li>`).join('')}</ul>`;
  }

  // Heart buttons read what they save from the card or PDP around them.
  function wishlistEntry(button) {
    const card = button.closest('.sf-card');
    const pdp = button.closest('.sf-pdp');
    const scope = card || pdp || document;
    const title = scope.querySelector('.sf-card__title a, .sf-pdp__title')?.textContent.trim() || '';
    const variant = pdp?.querySelector('input[name="product-variant"]:checked');
    const cardVariant = card?.dataset.cardVariantId || '';
    let image = card ? card.querySelector('.sf-card__img:not(.is-off), .sf-card__img') : pdp?.querySelector('[data-gallery-slide]:not([hidden]) img, [data-gallery-slide] img');
    if (pdp && variant?.dataset.imageIndex !== undefined) {
      const index = Number(variant.dataset.imageIndex);
      if (Number.isInteger(index)) image = document.querySelectorAll('[data-gallery-slide] img')[index] || image;
    }
    return {
      handle: button.dataset.wishlist,
      variant: variant?.value || cardVariant,
      variantLabel: variant?.dataset.variantLabel || card?.dataset.cardVariantLabel || '',
      title,
      vendor: scope.querySelector('.sf-card__brand, .sf-pdp__brand')?.textContent.trim() || '',
      priceLabel: scope.querySelector('.sf-price__current')?.textContent.trim() || '',
      image: image?.currentSrc || image?.getAttribute('src') || image?.dataset.src || ''
    };
  }
  function syncHearts() {
    const saved = new Set(store.wishlist.items().map((item) => item.handle));
    document.querySelectorAll('[data-wishlist]').forEach((button) => {
      const on = saved.has(button.dataset.wishlist);
      button.setAttribute('aria-pressed', String(on));
      button.classList.toggle('is-active', on);
    });
  }
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist]');
    if (!button || !WISHLIST_ENABLED) return;
    event.preventDefault();
    const entry = wishlistEntry(button);
    const added = store.wishlist.toggle(entry);
    toast(added
      ? `<strong>${escapeHtml(t('addedToFavorites'))}</strong><span>${escapeHtml(entry.title)}</span><a href="${escapeHtml(WISHLIST_URL)}">${escapeHtml(t('goToFavorites'))}</a>`
      : `<strong>${escapeHtml(t('removedFromFavorites'))}</strong><span>${escapeHtml(entry.title)}</span>`);
  });

  // Cart line controls (drawer and cart page).
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-line-step]');
    const remove = event.target.closest('[data-line-remove]');
    const unwish = event.target.closest('[data-wishlist-remove]');
    if (step) {
      const item = store.cart.items().find((entry) => lineKey(entry) === step.dataset.line);
      if (item) store.cart.setQuantity(step.dataset.line, Number(item.quantity) + Number(step.dataset.lineStep));
    }
    if (remove) store.cart.remove(remove.dataset.lineRemove);
    if (unwish) store.wishlist.remove(unwish.dataset.wishlistRemove);
  });

  // The header cart opens the mini cart; without JS it stays a /cart link.
  const cartDrawer = document.getElementById('CartDrawer');
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-drawer-open="#CartDrawer"]');
    if (link && cartDrawer && !event.metaKey && !event.ctrlKey && !event.shiftKey) event.preventDefault();
  });
  function openMiniCart() {
    const opener = document.querySelector('a[data-drawer-open="#CartDrawer"]');
    if (cartDrawer && opener && cartDrawer.hidden) opener.click();
  }

  // Header account menu (<details>): close on outside click or Escape.
  const accountMenu = document.querySelector('[data-account-menu]');
  if (accountMenu) {
    document.addEventListener('click', (event) => {
      if (accountMenu.open && !accountMenu.contains(event.target)) accountMenu.open = false;
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !accountMenu.open) return;
      accountMenu.open = false;
      accountMenu.querySelector('summary')?.focus();
    });
  }

  const cartRoot = document.querySelector('[data-cart-root]');
  const wishlistRoot = document.querySelector('[data-wishlist-root]');
  function refresh() {
    updateCount();
    syncHearts();
    if (store.cart.ready()) renderMiniCart();
    // The cart page arrives server-rendered from the real cart, so it is only
    // re-rendered once the live cart has been read back.
    if (cartRoot && store.cart.ready()) renderCartPage(cartRoot);
    if (wishlistRoot) renderWishlist(wishlistRoot);
    if (store.cart.ready()) syncDiscountQuote();
  }
  store.subscribe(refresh);
  refresh();
  store.cart.load();

  // Broken image sources (a CDN file that fails, a network error): the image is
  // swapped once for the placeholder inside its own box (width/height stay, so
  // nothing shifts) and never retried; a placeholder that fails too is hidden
  // over the tile's own ground. Images that failed before this script ran are
  // caught by the sweep below.
  function brokenImage(img) {
    if (!(img instanceof HTMLImageElement) || !img.closest('.sf')) return;
    if (img.dataset.sfBroken) { img.style.visibility = 'hidden'; return; }
    img.dataset.sfBroken = '1';
    img.removeAttribute('srcset');
    img.classList.add('sf-img--broken');
    if (PLACEHOLDER && img.getAttribute('src') !== PLACEHOLDER) img.src = PLACEHOLDER;
    else img.style.visibility = 'hidden';
  }
  document.addEventListener('error', (event) => brokenImage(event.target), true);
  // (An SVG without intrinsic size also reports naturalWidth 0, so SVGs are left
  // to the error event.)
  document.querySelectorAll('.sf img[src]').forEach((img) => { if (img.complete && img.naturalWidth === 0 && !img.dataset.sfBroken && !/\.svg(\?|$)/i.test(img.currentSrc || img.src)) brokenImage(img); });

  // Mega menu images carry data-src and load the first time their panel is
  // about to open (hover or keyboard focus), never with the page.
  document.querySelectorAll('[data-mega-item]').forEach((item) => {
    const load = () => item.querySelectorAll('img[data-src]').forEach((img) => {
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    });
    item.addEventListener('pointerenter', load, { once: true });
    item.addEventListener('focusin', load, { once: true });
  });

  // Product card gallery. The HTML holds only the cover; the other frames are
  // listed in data-images and become <img> elements the first time they are
  // needed (plus one ahead as a preload). One delegated listener per event
  // type serves every card on the page.
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest('[data-card-variant-choice]');
    if (!swatch) return;
    event.preventDefault();
    event.stopPropagation();
    const card = swatch.closest('.sf-card');
    if (!card) return;
    const image = card.querySelector('.sf-card__img:not(.is-off)') || card.querySelector('.sf-card__img');
    if (image && swatch.dataset.image) {
      image.src = swatch.dataset.image;
      if (swatch.dataset.imageSrcset) image.srcset = swatch.dataset.imageSrcset;
      else image.removeAttribute('srcset');
      delete image.dataset.sfLazySrc;
      delete image.dataset.sfLazySrcset;
    }
    const current = card.querySelector('[data-price-current]');
    const compare = card.querySelector('[data-price-compare]');
    if (current && swatch.dataset.priceLabel) current.textContent = swatch.dataset.priceLabel;
    if (compare) { compare.textContent = swatch.dataset.compareLabel || ''; compare.hidden = !swatch.dataset.compareLabel; }
    card.dataset.cardVariantId = swatch.dataset.cardVariantChoice;
    card.dataset.cardVariantLabel = swatch.dataset.variantLabel || '';
    const selectedLabel = card.querySelector('[data-card-variant-label]');
    if (selectedLabel) selectedLabel.textContent = swatch.dataset.variantLabel || '';
    card.querySelectorAll('.sf-card__link, .sf-card__title a').forEach((link) => {
      try { const url = new URL(link.href, window.location.href); url.searchParams.set('variant', swatch.dataset.cardVariantChoice); link.href = url.toString(); } catch { /* keep existing link */ }
    });
    card.querySelectorAll('[data-card-variant-choice]').forEach((button) => {
      const selected = button === swatch;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
  });

  function cardFrames(media) {
    if (!media._frames) {
      let list = [];
      try { list = JSON.parse(media.dataset.images || '[]'); } catch { list = []; }
      media._list = list;
      media._frames = [media.querySelector('.sf-card__img')];
      media._index = 0;
    }
    return media._frames;
  }
  function cardFrame(media, index) {
    const frames = cardFrames(media);
    if (!frames[index] && media._list[index]) {
      const [src, fit] = media._list[index];
      const img = document.createElement('img');
      img.className = `sf-card__img sf-card__img--${fit === 'cover' ? 'cover' : 'contain'} is-off`;
      img.alt = '';
      img.decoding = 'async';
      img.src = src;
      media.querySelector('.sf-card__link').appendChild(img);
      frames[index] = img;
    }
    return frames[index];
  }
  function showCardFrame(media, index) {
    cardFrames(media);
    const total = media._list.length;
    if (total < 2) return;
    const next = (index + total) % total;
    const target = cardFrame(media, next);
    media._frames.forEach((frame) => frame && frame.classList.toggle('is-off', frame !== target));
    media.querySelectorAll('.sf-card__dot').forEach((dot, i) => dot.classList.toggle('is-active', i === next));
    media._index = next;
    cardFrame(media, (next + 1) % total);
  }
  const stepCard = (media, step) => {
    cardFrames(media);
    showCardFrame(media, media._index + step);
  };
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-card-step]');
    if (!button) return;
    event.preventDefault();
    const media = button.closest('[data-card-gallery]');
    if (media) stepCard(media, Number(button.dataset.cardStep));
  });
  document.addEventListener('keydown', (event) => {
    const button = event.target.closest?.('[data-card-step]');
    if (!button || !/^Arrow(Left|Right)$/.test(event.key)) return;
    event.preventDefault();
    stepCard(button.closest('[data-card-gallery]'), event.key === 'ArrowRight' ? 1 : -1);
  });
  // Pointer devices: warm the second frame when the card is first hovered.
  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    const media = event.target.closest?.('[data-card-gallery]');
    if (media && !media._warm) { media._warm = true; cardFrames(media); cardFrame(media, 1); }
  }, { passive: true });
  // Touch: a horizontal swipe on the image changes the frame; a tap still
  // opens the product. Vertical scrolling is left to the browser (pan-y).
  let swipe = null;
  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse') return;
    const media = event.target.closest?.('[data-card-gallery]');
    swipe = media ? { media, x: event.clientX, y: event.clientY } : null;
  }, { passive: true });
  document.addEventListener('pointerup', (event) => {
    if (!swipe) return;
    const dx = event.clientX - swipe.x;
    const dy = event.clientY - swipe.y;
    const { media } = swipe;
    swipe = null;
    if (Math.abs(dx) < 36 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    stepCard(media, dx < 0 ? 1 : -1);
    media._swiped = Date.now();
  }, { passive: true });
  document.addEventListener('pointercancel', () => { swipe = null; }, { passive: true });
  document.addEventListener('click', (event) => {
    const media = event.target.closest('[data-card-gallery]');
    if (media && media._swiped && Date.now() - media._swiped < 400) event.preventDefault();
  }, true);

  // Rails (room tiles): previous/next scroll by one visible page; buttons are
  // disabled at either end.
  function railState(rail) {
    const max = rail.scrollWidth - rail.clientWidth - 2;
    document.querySelectorAll(`[data-rail-step][aria-controls="${rail.id}"]`).forEach((button) => {
      button.disabled = Number(button.dataset.railStep) < 0 ? rail.scrollLeft <= 2 : rail.scrollLeft >= max;
    });
  }
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-rail-step]');
    const rail = button && document.getElementById(button.getAttribute('aria-controls'));
    if (!rail) return;
    const item = rail.querySelector('.sf-rail__item');
    const stride = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(rail.firstElementChild).columnGap || '0') : rail.clientWidth;
    const perPage = Math.max(1, Math.floor((rail.clientWidth + 1) / stride));
    rail.scrollBy({ left: Number(button.dataset.railStep) * stride * perPage, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  });
  document.querySelectorAll('[data-rail]').forEach((rail) => {
    let frame = 0;
    rail.addEventListener('scroll', () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(() => railState(rail)); }, { passive: true });
    rail.addEventListener('keydown', (event) => {
      if (event.target !== rail || !/^Arrow(Left|Right)$/.test(event.key)) return;
      event.preventDefault();
      rail.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * rail.clientWidth * 0.8, behavior: 'smooth' });
    });
    railState(rail);
    window.addEventListener('resize', () => railState(rail), { passive: true });
  });

  // Compact long facet lists without introducing a nested scrollbar.
  function refreshFacetToggles(scope = document) {
    scope.querySelectorAll('[data-filter-options]').forEach((options) => {
      const visible = [...options.querySelectorAll(':scope > label')].filter((label) => !label.hidden);
      options.dataset.hasOverflow = String(visible.length > 6);
      const button = options.parentElement?.querySelector(':scope > [data-filter-toggle]');
      if (button) button.hidden = visible.length <= 6;
    });
  }
  refreshFacetToggles();
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-filter-toggle]');
    if (!button) return;
    const options = button.parentElement?.querySelector('[data-filter-options]');
    if (!options) return;
    const expand = options.dataset.collapsed === 'true';
    options.dataset.collapsed = String(!expand);
    button.textContent = expand ? button.dataset.less : button.dataset.more;
    button.setAttribute('aria-expanded', String(expand));
  });

})();
